/**
 * Common utility functions for the PMPI application
 */
import { CHARACTER_LIMITS, DATE_RANGES } from './appConstants';
import { COLORS } from './colors';

/**
 * Format Date as YYYY-MM-DD
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
export const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Subtract days from a date
 * @param {Date} date - The starting date
 * @param {number} days - Number of days to subtract
 * @returns {Date} - New date with days subtracted
 */
export const subtractDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
};

/**
 * Compute from_date and to_date based on timeframe
 * @param {string} timeframe - The timeframe constant
 * @returns {object} - Object with from_date and to_date
 */
export const getDateRangeFromTimeframe = (timeframe) => {
  const today = new Date();

  switch (timeframe) {
    case DATE_RANGES.TODAY: {
      const d = formatDate(today);
      return { from_date: d, to_date: d };
    }
    case DATE_RANGES.YESTERDAY: {
      const y = formatDate(subtractDays(today, 1));
      return { from_date: y, to_date: y };
    }
    case DATE_RANGES.THIS_WEEK: {
      const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
      const diffToMonday = (dayOfWeek + 6) % 7; // days since Monday
      const monday = subtractDays(today, diffToMonday);
      return { from_date: formatDate(monday), to_date: formatDate(today) };
    }
    case DATE_RANGES.THIS_MONTH: {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from_date: formatDate(firstDay), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_MONTH: {
      const firstDayPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDayPrevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        from_date: formatDate(firstDayPrevMonth),
        to_date: formatDate(lastDayPrevMonth),
      };
    }
    case DATE_RANGES.LAST_7_DAYS: {
      const from = subtractDays(today, 6);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_14_DAYS: {
      const from = subtractDays(today, 13);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_30_DAYS: {
      const from = subtractDays(today, 29);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_60_DAYS: {
      const from = subtractDays(today, 59);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_90_DAYS: {
      const from = subtractDays(today, 89);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_120_DAYS: {
      const from = subtractDays(today, 119);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    case DATE_RANGES.LAST_180_DAYS: {
      const from = subtractDays(today, 179);
      return { from_date: formatDate(from), to_date: formatDate(today) };
    }
    default:
      return { from_date: null, to_date: null };
  }
};

/**
 * Calculate text color based on background color luminance
 * @param {string} bgColor - Background color in hex format
 * @returns {string} - 'black' or 'white' based on contrast
 */
export const getContrastTextColor = (bgColor) => {
  if (!bgColor) return 'white';
  const hex = bgColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? 'black' : 'white';
};

export const stringToColor = (value) => {
  if (!value) return COLORS.gray[400];
  let hash = 0;
  const text = String(value);
  for (let i = 0; i < text.length; i += 1) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i += 1) {
    color += (`00${((hash >> (i * 8)) & 0xff).toString(16)}`).slice(-2);
  }
  return color;
};

const getPriorityOption = (priority, priorities = []) => {
  const priorityId = typeof priority === 'object'
    ? priority?.id ?? priority?.priority_id ?? priority?.value
    : priority;
  const priorityName = typeof priority === 'object'
    ? priority?.name ?? priority?.priority_name ?? priority?.label
    : priority;

  return priorities.find((item) => (
    (priorityId !== undefined && priorityId !== null && String(item.id) === String(priorityId)) ||
    (priorityName && item.name === priorityName)
  ));
};

export const getPriorityName = (priority, priorities = []) => {
  const option = getPriorityOption(priority, priorities);
  if (option?.name) return option.name;
  if (typeof priority === 'object') {
    return priority?.name ?? priority?.priority_name ?? priority?.label ?? '';
  }
  return priority || '';
};

export const getPriorityColor = (
  priority,
  { priorityColor, priorities = [], fallbackColor = COLORS.gray[400] } = {}
) => {
  const option = getPriorityOption(priority, priorities);
  const configuredColor = priorityColor || priority?.colour || priority?.color || option?.colour || option?.color;
  if (configuredColor) return configuredColor;

  const name = getPriorityName(priority, priorities);
  switch (name) {
    case 'Critical': return COLORS.priority.critical;
    case 'High': return COLORS.priority.high;
    case 'Medium': return COLORS.priority.medium;
    case 'Low': return COLORS.priority.low;
    default: return name ? stringToColor(name) : fallbackColor;
  }
};

/**
 * Get character limit for a specific field
 * @param {string} fieldName - The name of the field
 * @returns {number|null} - The character limit or null if no limit exists
 */
export const getFieldLimit = (fieldName) => {
  const fieldLimits = {
    'first_name': CHARACTER_LIMITS.USER_FIRST_NAME,
    'last_name': CHARACTER_LIMITS.USER_LAST_NAME,
    'email': CHARACTER_LIMITS.USER_EMAIL,
    'comment': CHARACTER_LIMITS.COMMENT,
    'description': CHARACTER_LIMITS.TICKET_DESCRIPTION,
    'space_description': CHARACTER_LIMITS.SPACE_DESCRIPTION,
    'project_description': CHARACTER_LIMITS.PROJECT_DESCRIPTION,
    'label_name': CHARACTER_LIMITS.LABEL_NAME,
    'task_type_name': CHARACTER_LIMITS.TASK_TYPE_NAME,
    'priority_name': CHARACTER_LIMITS.PRIORITY_NAME,
    'space_name': CHARACTER_LIMITS.SPACE_NAME,
    'project_name': CHARACTER_LIMITS.PROJECT_NAME
  };
  
  return fieldLimits[fieldName] || null;
};

/**
 * Handle character limit validation and truncation
 * @param {string} fieldName - The name of the field
 * @param {string} value - The input value
 * @param {boolean} hasInteracted - Whether user has interacted with the field
 * @returns {object} - Object containing processed value, warning state, and warning text
 */
export const handleCharacterLimit = (fieldName, value, hasInteracted = true) => {
  const limit = getFieldLimit(fieldName);
  if (!limit) return { value, showWarning: false };

  const truncatedValue = value.length > limit ? value.substring(0, limit) : value;
  const showWarning = truncatedValue.length >= limit && hasInteracted;
  
  return { 
    value: truncatedValue, 
    showWarning,
    warningText: showWarning ? `You are close to reaching the ${limit.toLocaleString()} character limit.` : null
  };
};

/**
 * Handle keydown events for character limit enforcement
 * @param {Event} e - The keydown event
 * @param {string} fieldName - The name of the field
 * @param {string} currentValue - The current field value
 * @param {function} setWarningCallback - Callback to set warning state
 * @returns {boolean} - Whether the key press should be allowed
 */
export const handleCharacterLimitKeyDown = (e, fieldName, currentValue, setWarningCallback) => {
  const limit = getFieldLimit(fieldName);
  if (!limit) return true;

  const currentLength = currentValue.length;
  // Allow navigation keys and actual deletion keys - they don't add characters
  const isDeletionKey = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key);
  const isModifierKey = e.ctrlKey || e.metaKey || e.altKey;
  
  if (currentLength >= limit && !isDeletionKey && !isModifierKey && e.key.length === 1) {
    e.preventDefault();
    // Show warning when user tries to type at limit
    if (setWarningCallback) {
      setWarningCallback(`You are close to reaching the ${limit.toLocaleString()} character limit.`);
    }
    return false;
  }
  
  return true;
};

/**
 * Handle paste events for character limit enforcement
 * @param {Event} e - The paste event
 * @param {string} fieldName - The name of the field
 * @param {string} currentValue - The current field value
 * @param {function} updateCallback - Callback to update the field value
 * @returns {boolean} - Whether the paste was handled
 */
export const handleCharacterLimitPaste = (e, fieldName, currentValue, updateCallback) => {
  const limit = getFieldLimit(fieldName);
  if (!limit) return false;

  e.preventDefault();
  const pastedText = e.clipboardData.getData('text/plain');
  const input = e.target;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  
  const beforeCursor = currentValue.substring(0, start);
  const afterCursor = currentValue.substring(end);
  
  const remainingChars = limit - beforeCursor.length - afterCursor.length;
  if (remainingChars <= 0) {
    return true; // Paste blocked - no space left
  }
  
  const truncatedPaste = pastedText.substring(0, remainingChars);
  
  if (updateCallback) {
    updateCallback(truncatedPaste);
  }
  
  return true; // Paste handled
};

export const clampTextToLimit = (value, limit) => String(value ?? '').slice(0, limit);

export const buildLimitedPasteValue = ({ currentValue = '', pastedText = '', selectionStart, selectionEnd, limit }) => {
  const safeCurrentValue = String(currentValue ?? '');
  const start = typeof selectionStart === 'number' ? selectionStart : safeCurrentValue.length;
  const end = typeof selectionEnd === 'number' ? selectionEnd : start;
  const nextValue = `${safeCurrentValue.slice(0, start)}${pastedText}${safeCurrentValue.slice(end)}`;

  return {
    value: clampTextToLimit(nextValue, limit),
    wasTruncated: nextValue.length > limit
  };
};

/**
 * Remove deleted suffixes from email addresses
 * @param {string} email - The email to clean
 * @returns {string} - Cleaned email address
 */
export const cleanDeletedEmail = (email = '') => {
  if (!email) return '';
  return email.replace(/[-_]deleted[-_]\d+$/, '').replace(/[-_]deleted$/, '');
};

export const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:  return 'st';
    case 2:  return 'nd';
    case 3:  return 'rd';
    default: return 'th';
  }
};
